import { FormEvent, useEffect, useState } from 'react'
import { Add, Delete, Download } from '@nine-thirty-five/material-symbols-react/sharp'
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  LinearProgress,
  LinkText,
  TextField,
  Tooltip,
  Typography
} from '@iziplay/ui-kit'

import Spinner from '../components/spinner'
import { MarqueeText } from '../components/marqueeText'
import { displayNotification, setCurrentPath } from '../state/ui'
import {
  ReadingDetails,
  bookCoverURL,
  problemDetail,
  useAddReading,
  useDeleteReading,
  useReadings
} from '../queries/books'

function BookCover({ isbn, title }: { isbn: string; title: string }) {
  const [failed, setFailed] = useState(false)

  if (failed) {
    return (
      <CardMedia
        style={{
          aspectRatio: '2 / 3',
          backgroundColor: 'var(--black)',
          backgroundImage: "url('/img/white-50.png')",
          backgroundSize: '160%',
          backgroundPosition: 'center'
        }}
        title={title}
      />
    )
  }

  return (
    <CardMedia
      component="img"
      image={bookCoverURL(isbn)}
      alt={title}
      style={{ aspectRatio: '2 / 3', objectFit: 'cover' }}
      onError={() => setFailed(true)}
    />
  )
}

function StatusChip({ status }: { status: ReadingDetails['status'] }) {
  switch (status) {
    case 'DOWNLOADED':
      return <Chip size="small" color="success" label="Ready" />
    case 'DOWNLOADING':
      return <Chip size="small" color="warning" label="Downloading…" />
    default:
      return <Chip size="small" label="Queued" />
  }
}

function BookCard({ details }: { details: ReadingDetails }) {
  const [hovered, setHovered] = useState(false)
  const deleteReading = useDeleteReading()

  const { reading, book, status, url } = details
  const title = book?.title || `ISBN ${reading.isbn}`

  function remove() {
    deleteReading.mutate(reading.id, {
      onError: err => displayNotification(problemDetail(err, 'Failed to remove the book'), 'error')
    })
  }

  return (
    <Card
      variant="outlined"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <BookCover isbn={reading.isbn} title={`Cover of ${title}`} />
      <CardContent>
        <Typography variant="body1" component="h2">
          <MarqueeText text={title} hovered={hovered} />
        </Typography>
        {book?.author && (
          <Typography variant="body2" component="p" style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
            {book.author}
          </Typography>
        )}
        {book?.year ? (
          <Typography variant="body2" color="textSecondary" component="p" style={{ opacity: 0.55 }}>
            {book.year}
          </Typography>
        ) : null}
        <div style={{ marginTop: '8px' }}>
          <StatusChip status={status} />
        </div>
        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ flex: 1 }}>
            <LinearProgress value={reading.progress} />
          </div>
          <Typography variant="body2" component="span" style={{ opacity: 0.55 }}>
            {reading.progress}%
          </Typography>
        </div>
      </CardContent>
      <CardActions>
        {url && status === 'DOWNLOADED' && (
          <Button href={url} variant="text" startIcon={<Download size={20} />}>
            EPUB
          </Button>
        )}
        <Tooltip title="Remove from my books">
          <IconButton onClick={remove} disabled={deleteReading.isPending} aria-label="Remove">
            <Delete size={20} />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  )
}

function AddBookCard() {
  const [isbn, setIsbn] = useState('')
  const addReading = useAddReading()

  // Tolerate the dashes and spaces ISBNs are printed with.
  const digits = isbn.replace(/[^0-9]/g, '')
  const valid = /^[0-9]{13}$/.test(digits)

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!valid) return

    addReading.mutate(digits, {
      onSuccess: () => {
        setIsbn('')
        displayNotification('Book added, we are looking for it!', 'success')
      },
      onError: err => displayNotification(problemDetail(err, 'Failed to add the book'), 'error')
    })
  }

  return (
    <Card variant="outlined" style={{ marginBottom: '12px' }}>
      <CardContent>
        Add a book with its ISBN-13 (on the back cover, next to the barcode) and we will fetch it
        for you. It then syncs to your Kobo e-readers, managed in{' '}
        <LinkText to="/settings?tab=kobo">Settings</LinkText>. The books already on the server are
        listed on the <LinkText to="/books">catalog</LinkText>.
      </CardContent>
      <CardActions>
        <form onSubmit={submit} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <TextField
            id="book-isbn"
            placeholder="9782070413110"
            value={isbn}
            onChange={e => setIsbn(e.target.value)}
            inputMode="numeric"
          />
          <Button
            type="submit"
            variant="text"
            startIcon={<Add size={24} />}
            disabled={!valid || addReading.isPending}
          >
            Add
          </Button>
        </form>
      </CardActions>
    </Card>
  )
}

export function MyLibrary() {
  const readings = useReadings()

  useEffect(() => {
    setCurrentPath(['Books', 'My library'])
  }, [])

  if (readings.isPending) {
    return <Spinner />
  }

  return (
    <div className="scrollable" style={{ paddingBottom: '12px' }}>
      <AddBookCard />
      {readings.error ? (
        <Card variant="outlined">
          <CardContent>Failed to load your books, sorry! Try again in a moment.</CardContent>
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '8px', padding: '12px', boxSizing: 'border-box' }}>
          {readings.data.map(details => (
            <BookCard key={details.reading.id} details={details} />
          ))}
        </div>
      )}
    </div>
  )
}
